import { useEffect, useRef, useCallback } from "react";

const DESKTOP_PARTICLE_COUNT = 42;
const MOBILE_PARTICLE_COUNT = 20;
const COLORS = [
  [197, 163, 78], // gold-500
  [212, 183, 106], // gold-400
  [196, 90, 45], // ember-500
  [232, 138, 90], // ember-300
];

class Particle {
  constructor(w, h) {
    this.canvasW = w;
    this.canvasH = h;
    this.reset(true);
  }

  reset(initial = false) {
    this.x = Math.random() * this.canvasW;
    this.y = initial
      ? Math.random() * this.canvasH
      : this.canvasH + Math.random() * 20;
    this.size = Math.random() * 1.5 + 0.5;
    this.speedY = Math.random() * 0.4 + 0.15;
    this.wobbleFreq = Math.random() * 0.008 + 0.003;
    this.wobbleAmp = Math.random() * 0.4 + 0.1;
    this.wobbleOffset = Math.random() * Math.PI * 2;
    this.maxOpacity = Math.random() * 0.42 + 0.12;
    this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
    this.age = 0;
  }

  update() {
    this.age++;
    this.y -= this.speedY;
    this.x +=
      Math.sin(this.age * this.wobbleFreq + this.wobbleOffset) * this.wobbleAmp;

    // Fade based on vertical position
    const progress = 1 - this.y / this.canvasH;
    let alpha = this.maxOpacity;
    if (progress < 0.15) alpha *= progress / 0.15;
    if (progress > 0.75) alpha *= (1 - progress) / 0.25;
    this.alpha = Math.max(0, alpha);

    if (this.y < -10) this.reset();
  }

  draw(ctx) {
    if (this.alpha <= 0.01) return;
    const [r, g, b] = this.color;

    // Core dot
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${this.alpha})`;
    ctx.fill();

    // Soft glow
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${this.alpha * 0.15})`;
    ctx.fill();
  }
}

const useParticles = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animIdRef = useRef(null);
  const activeRef = useRef(false);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const count =
      window.innerWidth >= 768 ? DESKTOP_PARTICLE_COUNT : MOBILE_PARTICLE_COUNT;
    particlesRef.current = [];
    for (let i = 0; i < count; i++) {
      particlesRef.current.push(new Particle(canvas.width, canvas.height));
    }
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particlesRef.current.forEach((p) => {
      p.update();
      p.draw(ctx);
    });
    animIdRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const start = () => {
      if (activeRef.current) return;
      activeRef.current = true;
      init();
      animate();
    };

    const stop = () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      cancelAnimationFrame(animIdRef.current);
    };

    const handleResize = () => {
      if (activeRef.current) {
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = window.innerWidth;
          canvas.height = window.innerHeight;
          particlesRef.current.forEach((p) => {
            p.canvasW = canvas.width;
            p.canvasH = canvas.height;
          });
        }
      }
    };

    start();

    window.addEventListener("resize", handleResize);
    return () => {
      stop();
      window.removeEventListener("resize", handleResize);
    };
  }, [init, animate]);

  return canvasRef;
};

export default useParticles;
